import { ComponentProps, useCallback } from 'react';
import { Controller } from 'react-hook-form';

import EventIcon from '@mui/icons-material/Event';
import { PickerValidDate } from '@mui/x-date-pickers/models/pickers';

import {
  DateRangePicker,
  TextFieldProps,
  useWindowedTooltipZIndex,
  withWindowedPopperProps,
} from '@sbv-clients/ui-shared';

import { useFormFieldError } from '../../hooks/use-field-error';

type PickerPropsType = {
  minDate?: PickerValidDate;
  maxDate?: PickerValidDate;
  onOpen?: () => void;
};

type TextFieldPropsType = {
  name: string;
  label?: TextFieldProps['label'];
  fullWidth?: TextFieldProps['fullWidth'];
  disabled?: TextFieldProps['disabled'];
  required?: TextFieldProps['required'];
  size?: TextFieldProps['size'];
  onChange?: () => void;
  onBlur?: () => void;
};

type FormDateRangePickerFieldProps = PickerPropsType & TextFieldPropsType;
type RenderType = ComponentProps<typeof Controller>['render'];
export const FormDateRangePickerField = ({
  name,
  label,
  fullWidth,
  disabled,
  required,
  size,
  minDate,
  maxDate,
  onOpen,
  onChange,
}: FormDateRangePickerFieldProps) => {
  const { error, helperText } = useFormFieldError(name);
  const windowedConfig = useWindowedTooltipZIndex();

  const render: RenderType = useCallback(
    ({ field }) => {
      const slotProps = {
        textField: {
          fullWidth,
          required,
          size,
          helperText,
          error,
        },
        popper: withWindowedPopperProps(windowedConfig, undefined),
      };
      return (
        <DateRangePicker
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          onOpen={onOpen}
          {...field}
          onChange={(value, context) => {
            field.onChange(value, context);
            onChange?.();
          }}
          slots={{ openPickerIcon: EventIcon }}
          slotProps={slotProps}
        />
      );
    },
    [
      disabled,
      error,
      fullWidth,
      helperText,
      maxDate,
      minDate,
      onChange,
      onOpen,
      required,
      size,
      windowedConfig,
    ]
  );

  return <Controller name={name} render={render} />;
};
